import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes = [
  // ── Auth (sin sidebar) ──────────────────────────────────────────────────
  {
    path: '/auth/login',
    name: 'auth-login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/auth/register',
    name: 'auth-register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { public: true },
  },
  {
    path: '/auth/forgot-password',
    name: 'auth-forgot-password',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/auth/reset-password',
    name: 'auth-reset-password',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/auth/must-reset',
    name: 'auth-must-reset',
    component: () => import('@/views/auth/MustResetPasswordView.vue'),
    meta: { public: true },
  },

  // ── App (con sidebar) ───────────────────────────────────────────────────
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    children: [
      // === Home ===
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/dashboard/HomeView.vue')
      },

      // === Newsletters ===
      {
        path: 'newsletters',
        name: 'newsletters',
        component: () => import('@/views/newsletters/NewslettersList.vue')
      },
      {
        path: 'newsletters/compose',
        name: 'newsletter-compose',
        component: () => import('@/views/newsletters/NewsletterCompose.vue')
      },
      {
        path: 'newsletters/analytics',
        name: 'newsletter-analytics',
        component: () => import('@/views/newsletters/NewsletterAnalyticsView.vue')
      },
      {
        path: 'editorial',
        name: 'editorial',
        component: () => import('@/views/editorial/EditorialView.vue')
      },
      {
        path: 'newsletters/:id/stats',
        name: 'newsletter-stats',
        component: () => import('@/views/email-queue/EmailStatsView.vue')
      },
      {
        path: 'newsletters/:id',
        name: 'newsletter-edit',
        component: () => import('@/views/newsletters/NewsletterCompose.vue')
      },

      // === Newsletters > Automations ===
      {
        path: 'newsletters/automations',
        name: 'automations',
        component: () => import('@/views/automations/AutomationsView.vue')
      },
      {
        path: 'newsletters/automations/new',
        name: 'automation-new',
        component: () => import('@/views/automations/AutomationEditorView.vue')
      },
      {
        path: 'newsletters/automations/:id',
        name: 'automation-edit',
        component: () => import('@/views/automations/AutomationEditorView.vue')
      },

      // === Subscribers ===
      {
        path: 'subscribers',
        name: 'subscribers',
        component: () => import('@/views/subscribers/SubscribersView.vue')
      },
      {
        path: 'subscribers/:id',
        name: 'subscriber-detail',
        component: () => import('@/views/subscribers/SubscriberProfileView.vue')
      },
      {
        path: 'subscribers/lists',
        name: 'lists',
        component: () => import('@/components/lists/ListManager.vue')
      },
      {
        path: 'subscribers/lists/:id',
        name: 'list-detail',
        component: () => import('@/views/lists/ListDetailView.vue')
      },
      {
        path: 'subscribers/lists/:listId/forms/:id',
        name: 'form-detail',
        component: () => import('@/views/forms/FormDetailView.vue')
      },
      {
        path: 'subscribers/lists/:listId/fields',
        name: 'list-field-definitions',
        component: () => import('@/views/settings/FieldDefinitionsView.vue'),
        props: true,
      },
      {
        path: 'subscribers/lists/:listId/segments',
        name: 'list-segments',
        component: () => import('@/views/segments/SegmentsView.vue'),
        props: true,
      },
      {
        path: 'subscribers/lists/:listId/segments/new',
        name: 'list-segment-new',
        component: () => import('@/views/segments/SegmentBuilderView.vue'),
        props: true,
      },
      {
        path: 'subscribers/lists/:listId/segments/:id',
        name: 'list-segment-edit',
        component: () => import('@/views/segments/SegmentBuilderView.vue'),
        props: true,
      },
      {
        path: 'subscribers/segments',
        name: 'segments',
        component: () => import('@/views/segments/SegmentsView.vue')
      },
      {
        path: 'subscribers/segments/new',
        name: 'segment-new',
        component: () => import('@/views/segments/SegmentBuilderView.vue')
      },
      {
        path: 'subscribers/segments/:id',
        name: 'segment-edit',
        component: () => import('@/views/segments/SegmentBuilderView.vue')
      },
      {
        path: 'subscribers/fields',
        name: 'field-definitions',
        component: () => import('@/views/settings/FieldDefinitionsView.vue')
      },

      // === Settings ===
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/settings/SettingsView.vue')
      },
      {
        path: 'settings/deliverability',
        name: 'deliverability',
        component: () => import('@/views/DeliverabilityView.vue')
      },
      {
        path: 'settings/domain',
        name: 'domain-setup',
        component: () => import('@/views/DomainSetupView.vue'),
      },

      // === Redirects de compatibilidad ===
      { path: 'email-queue', redirect: '/newsletters' },
      { path: 'email-queue/new', redirect: '/newsletters/compose' },
      { path: 'email-queue/:id/stats', redirect: to => `/newsletters/${to.params.id}/stats` },
      { path: 'email-queue/:id', redirect: to => `/newsletters/${to.params.id}` },
      { path: 'instant-email', redirect: '/newsletters' },
      { path: 'instant-email/new', redirect: '/newsletters/compose' },
      { path: 'instant-email/:id', redirect: to => `/newsletters/${to.params.id}` },
      { path: 'lists', redirect: '/subscribers/lists' },
      { path: 'lists/:id', redirect: to => `/subscribers/lists/${to.params.id}` },
      { path: 'segments', redirect: '/subscribers/segments' },
      { path: 'segments/new', redirect: '/subscribers/segments/new' },
      { path: 'segments/:id', redirect: to => `/subscribers/segments/${to.params.id}` },
      { path: 'field-definitions', redirect: '/subscribers/fields' },
      { path: 'automations', redirect: '/newsletters/automations' },
      { path: 'automations/new', redirect: '/newsletters/automations/new' },
      { path: 'automations/:id', redirect: to => `/newsletters/automations/${to.params.id}` },
      { path: 'ab-tests', redirect: '/newsletters' },
      { path: 'ab-tests/new', redirect: '/newsletters/compose' },
      { path: 'ab-tests/:id', redirect: '/newsletters' },
      { path: 'forms', redirect: '/subscribers/lists' },
      { path: 'forms/:id', redirect: '/subscribers/lists' },
      { path: 'settings/forms', redirect: '/subscribers/lists' },
      { path: 'settings/forms/:id', redirect: '/subscribers/lists' },
      { path: 'deliverability', redirect: '/settings/deliverability' },
      { path: 'domain-setup', redirect: '/settings/domain' },
      { path: 'analytics', redirect: { name: 'newsletter-analytics' } }
    ]
  },
  {
    path: '/invite/:token',
    name: 'invite-accept',
    component: () => import('@/views/InviteAcceptView.vue'),
    meta: { public: true }
  },
  // El wizard /onboarding era zombi (nadie enlazaba a él y su paso "Activar
  // proveedor" ya no activaba nada tras desactivar dorilon_native). El onboarding
  // real vive en HomeView → OnboardingChecklist, que refleja estado verdadero y
  // enlaza a suscriptores / ajustes / compose. El catch-all manda /onboarding a la home.

  // ── Catch-all: cualquier URL desconocida vuelve a la home ─────────────────
  // Evita que una ruta vieja o un typo dejen la SPA en blanco absoluto.
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' }
  },
]

const router = createRouter({
  history: createWebHistory('/'),
  routes
})

// ── Navigation guard ────────────────────────────────────────────────────────
// Un único intento de fetchCurrentUser por sesión de navegación
let authChecked = false

router.beforeEach(async (to) => {
  // Rutas de auth siempre accesibles sin comprobación
  if (to.meta.public) return true

  const authStore = useAuthStore()

  // Comprobar sesión solo la primera vez (evita llamadas en cada navegación)
  if (!authChecked) {
    await authStore.fetchCurrentUser()
    authChecked = true
  }

  if (!authStore.isAuthenticated) {
    return { name: 'auth-login', query: { redirect: to.fullPath !== '/' ? to.fullPath : undefined } }
  }

  // Usuario autenticado pero con contraseña pendiente de resetear
  if (authStore.mustResetPassword && to.name !== 'auth-must-reset') {
    return { name: 'auth-must-reset', query: { email: authStore.user?.email } }
  }

  return true
})

export default router
